import React, { useCallback, useState } from "react";
import { useNow } from "../hooks/useNow";
import { VideoJob } from "../firebase/video";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  Film,
  Image,
  Loader2,
  RotateCcw,
  Timer,
  Volume2,
  XCircle,
} from "lucide-react";

import { ref, getBlob } from "firebase/storage";
import { storage } from "../firebase/config";

interface VideoCardProps {
  job: VideoJob & { id: string };
  onRetry: (job: VideoJob) => void;
}

function toMillis(createdAt: any): number {
  return typeof createdAt.toMillis === "function"
    ? createdAt.toMillis()
    : typeof createdAt === "number"
      ? createdAt
      : new Date(createdAt).getTime();
}

function formatElapsed(createdAt: any, now: number): string {
  if (!createdAt) return "";
  const elapsed = Math.max(0, Math.floor((now - toMillis(createdAt)) / 1000));
  if (elapsed < 60) return `${elapsed}s ago`;
  if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m ago`;
  if (elapsed < 86400) return `${Math.floor(elapsed / 3600)}h ago`;
  return `${Math.floor(elapsed / 86400)}d ago`;
}

function friendlyModelName(job: VideoJob): string {
  if (job.modelName) return job.modelName;
  const parts = job.model.split("/");
  const name = parts[parts.length - 1];
  return name.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function friendlyMode(mode: string | undefined): string {
  if (mode === "frame_to_video") return "Frame guided";
  if (mode === "reference_to_video") return "Reference guided";
  return "Text";
}

function friendlyError(error: string | null): string {
  if (!error) return "An unknown error occurred.";
  const lower = error.toLowerCase();
  if (lower.includes("moderation") || lower.includes("flagged")) {
    return "Your prompt was flagged by content moderation. Please revise and try again.";
  }
  if (lower.includes("expired")) {
    return "Generation expired before completing. Please try again.";
  }
  return error;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<
    string,
    { bg: string; text: string; icon: React.ReactNode }
  > = {
    pending: {
      bg: "bg-warning-soft",
      text: "text-warning",
      icon: <Clock size={12} />,
    },
    in_progress: {
      bg: "bg-accent-soft",
      text: "text-accent",
      icon: <Loader2 size={12} className="animate-spin" />,
    },
    completed: {
      bg: "bg-success-soft",
      text: "text-success",
      icon: <CheckCircle2 size={12} />,
    },
    failed: {
      bg: "bg-danger-soft",
      text: "text-danger",
      icon: <XCircle size={12} />,
    },
    expired: {
      bg: "bg-raised",
      text: "text-muted",
      icon: <Timer size={12} />,
    },
    cancelled: {
      bg: "bg-raised",
      text: "text-muted",
      icon: <XCircle size={12} />,
    },
  };
  const c = config[status] ?? config.pending;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${c.bg} ${c.text}`}
    >
      {c.icon}
      {status.replace("_", " ")}
    </span>
  );
};

const VideoCard: React.FC<VideoCardProps> = ({ job, onRetry }) => {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const isActive = job.status === "pending" || job.status === "in_progress";

  const handleDownload = useCallback(async () => {
    if (!job.videoUrl) return;
    setDownloading(true);
    try {
      const storagePath = `generated-videos/${job.id}/${job.fileName || `video-${job.id}.mp4`}`;
      const fileRef = ref(storage, storagePath);
      const blob = await getBlob(fileRef);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = job.fileName || `video-${job.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(job.videoUrl, "_blank");
    } finally {
      setDownloading(false);
    }
  }, [job.videoUrl, job.fileName, job.id]);

  const isFailed =
    job.status === "failed" ||
    job.status === "expired" ||
    job.status === "cancelled";

  const now = useNow(isActive ? 5_000 : 60_000);
  const elapsed = formatElapsed(job.createdAt, now);
  const isTimedOut =
    isActive && !!job.createdAt && now - toMillis(job.createdAt) > 10 * 60 * 1000;

  const truncatedPrompt =
    job.prompt.length > 96 ? `${job.prompt.slice(0, 96)}...` : job.prompt;
  const providerParameterCount = Object.values(job.providerParameters ?? {}).filter(
    (value) => value !== null && value !== "",
  ).length;

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-sm">
      <div className="relative aspect-video bg-media flex items-center justify-center">
        {job.status === "completed" && job.videoUrl ? (
          <video
            key={job.videoUrl}
            src={job.videoUrl}
            controls
            preload="metadata"
            className="motion-fade w-full h-full object-contain"
          />
        ) : isActive ? (
          <div className="text-center text-on-solid">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-2 text-on-media" />
            <p className="text-sm text-on-media">
              {job.status === "in_progress" ? "Generating video" : "Queued"}
            </p>
            {isTimedOut && (
              <p className="text-xs text-on-media mt-1">
                Taking longer than expected
              </p>
            )}
          </div>
        ) : (
          <div className="text-center text-on-media">
            <Film className="w-10 h-10 mx-auto mb-2 text-on-media" />
            <p className="text-sm">
              {isFailed ? "Generation failed" : "No video"}
            </p>
          </div>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <StatusBadge status={job.status} />
          <span className="text-xs text-muted">{elapsed}</span>
        </div>

        <div>
          <p className="truncate text-sm font-semibold text-content">
            {job.sceneTitle || "Untitled shot"}
          </p>
          <p className="truncate text-xs font-medium text-muted">
            {friendlyModelName(job)}
          </p>
        </div>

        <div>
          <p className="text-sm text-content">
            {expanded ? job.prompt : truncatedPrompt}
          </p>
          {job.prompt.length > 96 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-accent hover:text-accent flex items-center gap-0.5 mt-0.5"
            >
              {expanded ? (
                <>
                  Less <ChevronUp size={12} />
                </>
              ) : (
                <>
                  More <ChevronDown size={12} />
                </>
              )}
            </button>
          )}
        </div>

        {isFailed && job.error && (
          <div className="flex items-start gap-2 p-2 rounded-lg bg-danger-soft text-danger text-xs">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{friendlyError(job.error)}</span>
          </div>
        )}

        {job.cost != null && job.status === "completed" && (
          <p className="text-xs text-muted">Cost: ${job.cost.toFixed(3)}</p>
        )}

        <div className="flex flex-wrap gap-1.5">
          <span className="px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
            {friendlyMode(job.generationMode)}
          </span>
          {job.duration && (
            <span className="px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
              {job.duration}s
            </span>
          )}
          {job.size ? (
            <span className="px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
              {job.size}
            </span>
          ) : (
            <>
              {job.resolution && (
                <span className="px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
                  {job.resolution}
                </span>
              )}
              {job.aspectRatio && (
                <span className="px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
                  {job.aspectRatio}
                </span>
              )}
            </>
          )}
          {job.generateAudio && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
              <Volume2 size={11} />
              Audio
            </span>
          )}
          {job.seed != null && (
            <span className="px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
              Seed {job.seed}
            </span>
          )}
          {Boolean(job.frameImageUrls?.length) && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
              <Image size={11} />
              {job.frameImageUrls?.length} frame
            </span>
          )}
          {Boolean(job.inputReferenceUrls?.length) && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
              <Image size={11} />
              {job.inputReferenceUrls?.length} ref
            </span>
          )}
          {providerParameterCount > 0 && (
            <span className="px-2 py-0.5 rounded-md bg-raised text-muted text-xs">
              {providerParameterCount} provider
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          {job.status === "completed" && job.videoUrl && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-soft text-accent text-xs font-medium hover:bg-accent-soft transition-colors disabled:opacity-50"
            >
              {downloading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              {downloading ? "Downloading..." : "Download"}
            </button>
          )}
          {isFailed && (
            <button
              type="button"
              onClick={() => onRetry(job)}
              title="Loads this clip's settings into the form so you can adjust and generate again"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-raised text-content text-xs font-medium hover:bg-pressed active:bg-pressed transition-colors duration-150 focus-visible:outline-offset-2"
            >
              <RotateCcw size={14} aria-hidden="true" />
              Edit and retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
